/**
 * SuggestionChips Component
 * 
 * Displays a horizontal scrollable row of suggestion chips with navigation chevrons.
 * Users can scroll through available actions and click on enabled suggestions.
 * Features smooth scrolling, chevron navigation, and responsive design.
 */
import React, { useRef, useState } from 'react'
import styles from './SuggestionChips.module.css'
import { useSnackbar } from 'react-simple-snackbar'

interface SuggestionChipsProps {
    onChipClick: (command: string) => void
}

const options = {
    position: 'bottom-right',
}

const suggestions = [
    { text: 'Retrigger Billing for ', icon: 'refresh', disabled: false },
    { text: 'Geocode', icon: 'location', disabled: false },  
    { text: 'Push To Driver (No Notification)', icon: 'send', disabled: true },
    { text: 'Push To Driver (With Notification)', icon: 'notification', disabled: true },
    { text: 'Create new order', icon: 'add', disabled: true },
    { text: 'Schedule Order', icon: 'calendar', disabled: true },
    { text: 'Reschedule Order', icon: 'calendar', disabled: true },
    { text: 'Schedule installation', icon: 'tools', disabled: true },
    { text: 'Create BOL', icon: 'document', disabled: true },
    { text: 'Move Back To Unassigned', icon: 'archive', disabled: true },
    { text: 'Cancel Order', icon: 'cancel', disabled: true },
    { text: 'Launch Robocall/Text', icon: 'phone', disabled: true },
    { text: 'Prints', icon: 'print', disabled: true },
    { text: 'Create Return To Store', icon: 'store', disabled: true },
    { text: 'Create Pickup From Store', icon: 'store', disabled: true },
    { text: 'Add pickup', icon: 'archive', disabled: true },
    { text: 'Launch Robocall/Text', icon: 'phone', disabled: true },
]

const SuggestionChips = ({ onChipClick }: SuggestionChipsProps) => {
    const [openSnackbar] = useSnackbar(options)
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const [showLeftChevron, setShowLeftChevron] = useState(false)
    const [showRightChevron, setShowRightChevron] = useState(true)

    const handleChipClick = (suggestion: (typeof suggestions)[0]) => {
        if (!suggestion.disabled) {
            onChipClick(suggestion.text)
        }
    }

    const scrollLeft = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' })
            updateChevronVisibility()
        }
    }

    const scrollRight = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' })
            updateChevronVisibility()
        }
    }

    const updateChevronVisibility = () => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current
            setShowLeftChevron(scrollLeft > 0)
            setShowRightChevron(scrollLeft < scrollWidth - clientWidth - 1)
        }
    }

    const handleScroll = () => {
        updateChevronVisibility()
    }

    return (
        <div className={styles.suggestionsContainer}>
            {/* Left Chevron */}
            {showLeftChevron && (
                <button
                    onClick={scrollLeft}
                    className={`${styles.chevronButton} ${styles.chevronLeft}`}
                    aria-label="Scroll left"
                >
                    <span className="icon is-small">
                        <svg viewBox="0 0 20 20" focusable="false" aria-hidden="true">
                            <path
                                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                fill="currentColor"
                            />
                        </svg>
                    </span>
                </button>
            )}

            {/* Right Chevron */}
            {showRightChevron && (
                <button
                    onClick={scrollRight}
                    className={`${styles.chevronButton} ${styles.chevronRight}`}
                    aria-label="Scroll right"
                >
                    <span className="icon is-small">
                        <svg viewBox="0 0 20 20" focusable="false" aria-hidden="true">
                            <path
                                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                fill="currentColor"
                            />
                        </svg>
                    </span>
                </button>
            )}

            {/* Scrollable Container */}
            <div 
                ref={scrollContainerRef}
                onScroll={handleScroll}
                className={styles.scrollableContainer}
            >
                {/* Suggestion Chips */}
                {suggestions.map((suggestion, index) => (
                    <button
                        key={suggestion.text}
                        className={`button is-small is-rounded is-bordered m-1 ${suggestion.disabled ? 'is-light is-static' : 'is-white'} ${styles.chip} ${suggestion.disabled ? styles.disabled : ''}`}
                        style={{ 
                            animationDelay: `${index * 30}ms`,
                            opacity: suggestion.disabled ? 0.5 : 1,
                            cursor: suggestion.disabled ? 'not-allowed' : 'pointer',
                            whiteSpace: 'nowrap',
                            flexShrink: 0
                        }}
                        onClick={() => handleChipClick(suggestion)}
                        disabled={suggestion.disabled}
                        aria-disabled={suggestion.disabled}
                    >
                        <span className="icon is-small">
                            <svg viewBox="0 0 20 20" focusable="false" aria-hidden="true">
                                {suggestion.icon === 'document' && (
                                    <path
                                        d="M13 2H5a1 1 0 00-1 1v14a1 1 0 001 1h10a1 1 0 001-1V5l-3-3zm-1 2l2 2h-2V4zM6 16V4h4v4h4v8H6z"
                                        fill="currentColor"
                                    />
                                )}
                                {suggestion.icon === 'store' && (
                                    <path
                                        d="M17 8.5l-1.4-4.2A1 1 0 0014.7 3H5.3a1 1 0 00-.9 1.3L3 8.5V9c0 1.1.9 2 2 2h.5c.5 1.2 1.7 2 3 2s2.5-.8 3-2h1c.5 1.2 1.7 2 3 2s2.5-.8 3-2h.5c1.1 0 2-.9 2-2v-.5zM8.5 11c-.8 0-1.5-.7-1.5-1.5S7.7 8 8.5 8s1.5.7 1.5 1.5S9.3 11 8.5 11zm6 0c-.8 0-1.5-.7-1.5-1.5S13.7 8 14.5 8s1.5.7 1.5 1.5-.7 1.5-1.5 1.5z"
                                        fill="currentColor"
                                    />
                                )}
                                {suggestion.icon === 'refresh' && (
                                    <path
                                        d="M16.95 8.464a1 1 0 11-2-.328 5 5 0 10-.777 4.875A1 1 0 1112.45 14.5a7 7 0 114.5-6.036z"
                                        fill="currentColor"
                                    />
                                )}
                                {suggestion.icon === 'cancel' && (
                                    <path
                                        d="M10 2a8 8 0 110 16 8 8 0 010-16zm0 2a6 6 0 100 12 6 6 0 000-12zm2.293 3.293L10 9.586 7.707 7.293a1 1 0 00-1.414 1.414L8.586 11l-2.293 2.293a1 1 0 101.414 1.414L10 12.414l2.293 2.293a1 1 0 001.414-1.414L11.414 11l2.293-2.293a1 1 0 00-1.414-1.414z"
                                        fill="currentColor"
                                    />
                                )}
                                {suggestion.icon === 'phone' && (
                                    <path
                                        d="M15.45 14.95c-.95.7-2.05 1.05-3.25 1.05-3.85 0-7-3.15-7-7s3.15-7 7-7 7 3.15 7 7c0 1.2-.35 2.3-1.05 3.25l1.45 1.45c.9-1.2 1.45-2.7 1.45-4.35 0-4.95-4.05-9-9-9s-9 4.05-9 9 4.05 9 9 9c1.65 0 3.15-.55 4.35-1.45l-1.45-1.45z"
                                        fill="currentColor"
                                    />
                                )}
                                {suggestion.icon === 'send' && (
                                    <path
                                        d="M3.5 8.3L2 9v6l1.5.7 11.7-3.8L17 11l-1.8-.9L3.5 8.3zm1.5.2l9.5 1.6L5 11.7V8.5z"
                                        fill="currentColor"
                                    />
                                )}
                                {suggestion.icon === 'notification' && (
                                    <path
                                        d="M10 2a6 6 0 00-6 6v2.5l-2 2v1h16v-1l-2-2V8a6 6 0 00-6-6zm2 14H8a2 2 0 004 0z"
                                        fill="currentColor"
                                    />
                                )}
                                {suggestion.icon === 'print' && (
                                    <path
                                        d="M4.5 8H4v5h1V9.5h11V13h1V8h-.5A1.5 1.5 0 0114 6.5v-4A1.5 1.5 0 0112.5 1h-5A1.5 1.5 0 006 2.5v4A1.5 1.5 0 004.5 8zm1-5.5a.5.5 0 01.5-.5h8a.5.5 0 01.5.5v4a.5.5 0 01-.5.5h-8a.5.5 0 01-.5-.5v-4zM15 14H5v-3h10v3z"
                                        fill="currentColor"
                                    />
                                )}
                                {suggestion.icon === 'calendar' && (
                                    <path
                                        d="M17 2a1 1 0 011 1v14a1 1 0 01-1 1H3a1 1 0 01-1-1V3a1 1 0 011-1h14zm-1 2H4v12h12V4zm-2 3a1 1 0 011 1v3a1 1 0 01-1 1H9a1 1 0 01-1-1V8a1 1 0 011-1h5zm0 2H9v1h5V9z"
                                        fill="currentColor"
                                    />
                                )}
                                {suggestion.icon === 'add' && (
                                    <path
                                        d="M10 2a8 8 0 110 16 8 8 0 010-16zm0 2a6 6 0 100 12 6 6 0 000-12zm1 2v3h3a1 1 0 010 2h-3v3a1 1 0 01-2 0v-3H6a1 1 0 010-2h3V6a1 1 0 012 0z"
                                        fill="currentColor"
                                    />
                                )}
                                {suggestion.icon === 'archive' && (
                                    <path
                                        d="M17 2a1 1 0 011 1v14a1 1 0 01-1 1H3a1 1 0 01-1-1V3a1 1 0 011-1h14zm-1 2H4v12h12V4zm-2 3a1 1 0 011 1v3a1 1 0 01-1 1H9a1 1 0 01-1-1V8a1 1 0 011-1h5zm0 2H9v1h5V9z"
                                        fill="currentColor"
                                    />
                                )}
                                {suggestion.icon === 'location' && (
                                    <path
                                        d="M10 2a6 6 0 016 6c0 3.314-2.686 6-6 6s-6-2.686-6-6a6 6 0 016-6zm0 2a4 4 0 100 8 4 4 0 000-8zm0 2a2 2 0 110 4 2 2 0 010-4z"
                                        fill="currentColor"
                                    />
                                )}
                                {suggestion.icon === 'tools' && (
                                    <path
                                        d="M17.1 14.9l-5.6-5.6c1.4-2.3.8-5.3-1.4-7.1-2.3-1.9-5.6-1.8-7.8.3-2.2 2.1-2.3 5.4-.3 7.7 1.8 2.1 4.8 2.7 7.1 1.4l5.6 5.6c.3.3.8.3 1.1 0l1.4-1.4c.3-.3.3-.8-.1-1zM3.7 6.7c0-1.7 1.4-3 3-3s3 1.4 3 3-1.4 3-3 3-3-1.3-3-3z"
                                        fill="currentColor"
                                    />
                                )}
                                {suggestion.icon === 'truck' && (
                                    <path
                                        d="M17 8h-1V5a1 1 0 00-1-1H3a1 1 0 00-1 1v10a1 1 0 001 1h1.14a2.5 2.5 0 004.72 0h2.28a2.5 2.5 0 004.72 0H17a1 1 0 001-1V9a1 1 0 00-1-1zm-1 6h-1.14a2.5 2.5 0 00-4.72 0H7.86a2.5 2.5 0 00-4.72 0H3V5h12v9zm1-5v3h-2V9h2z"
                                        fill="currentColor"
                                    />
                                )}
                                {suggestion.icon === 'report' && (
                                    <path
                                        d="M17 2a1 1 0 011 1v14a1 1 0 01-1 1H3a1 1 0 01-1-1V3a1 1 0 011-1h14zm-1 2H4v12h12V4zM8 9a1 1 0 011 1v3a1 1 0 01-2 0v-3a1 1 0 011-1zm4-3a1 1 0 011 1v6a1 1 0 01-2 0V7a1 1 0 011-1zm-6 9h8a1 1 0 010 2H6a1 1 0 010-2z"
                                        fill="currentColor"
                                    />
                                )}
                            </svg>
                        </span>
                        <span>{suggestion.text}</span>
                    </button>
                ))}
            </div>
        </div>
    )
}

export default React.memo(SuggestionChips)
